import {AcknowledgmentConfig} from '@app/acknowledgment/acknowledgment-config';
import {useLogout} from '@common/auth/requests/logout';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {useMutation} from '@tanstack/react-query';
import {Button} from '@ui/buttons/button';
import {Checkbox} from '@ui/forms/toggle/checkbox';
import {Trans} from '@ui/i18n/trans';
import {ArrowDownwardIcon} from '@ui/icons/material/ArrowDownward';
import {DescriptionIcon} from '@ui/icons/material/Description';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {LockIcon} from '@ui/icons/material/Lock';
import {TaskAltIcon} from '@ui/icons/material/TaskAlt';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {useSettings} from '@ui/settings/use-settings';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {createBrowserRouter} from 'react-router';

// mounted INSTEAD of the app router (common-provider) while a Main User has
// not accepted the current terms, the same takeover mechanism the email
// verification page uses - so there is no route to guard and nothing else
// to click. The server keeps the API closed until acceptance, this page is
// just the friendly face of that.
export const acknowledgmentRouter = createBrowserRouter([
  {
    path: '*',
    element: <AcknowledgmentPage />,
    hydrateFallbackElement: <FullPageLoader screen />,
  },
]);

export function AcknowledgmentPage({previewMode = false}: {previewMode?: boolean}) {
  const settings = useSettings();
  const config = (settings.acknowledgment ?? {}) as AcknowledgmentConfig;
  const {
    branding: {logo_light, logo_dark},
  } = settings;
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;
  const logout = useLogout();

  // the boss rule: the checkbox only unlocks once the whole document has
  // been scrolled through. A document short enough to need no scrollbar
  // counts as read immediately (the mount check below).
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [emailCopy, setEmailCopy] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const checkReachedEnd = useCallback(() => {
    const el = scrollerRef.current;
    if (el) {
      const scrollableDistance = el.scrollHeight - el.clientHeight;
      const nextProgress =
        scrollableDistance <= 0
          ? 100
          : Math.min(100, (el.scrollTop / scrollableDistance) * 100);

      setScrollProgress(nextProgress);
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
        setReachedEnd(true);
      }
    }
  }, []);

  useEffect(() => {
    checkReachedEnd();
  }, [checkReachedEnd]);

  const accept = useMutation({
    mutationFn: () =>
      apiClient
        .post('acknowledgment/accept', {email_copy: emailCopy})
        .then(r => r.data),
    onSuccess: () => {
      // bootstrap data is what carries the acceptance state, so a full
      // reload is what dismisses this page and lands on the drive
      window.location.reload();
    },
    onError: err => showHttpErrorToast(err),
  });

  return (
    <div className="relative isolate min-h-screen w-full overflow-hidden bg-alt px-16 py-20 sm:px-24 sm:py-32">
      <div className="size-420 pointer-events-none absolute -left-160 -top-160 rounded-full bg-primary/10 blur-[100px]" />
      <div className="size-480 pointer-events-none absolute -bottom-200 -right-140 rounded-full bg-positive/10 blur-[120px]" />

      <div className="relative mx-auto w-full max-w-[1080px]">
        <header className="mb-20 flex min-h-44 items-center justify-between gap-16">
          {logoSrc ? (
            <img src={logoSrc} alt="Site logo" className="block h-42 w-auto" />
          ) : (
            <div />
          )}
          <Button
            variant="outline"
            onClick={() => {
              if (previewMode) {
                // opened in a new tab from the settings page: close it, or
                // fall back to the settings page when the browser refuses
                window.close();
                window.setTimeout(() => {
                  window.location.href = '/admin/settings/acknowledgment';
                }, 150);
              } else {
                logout.mutate();
              }
            }}
            startIcon={<KeyboardArrowLeftIcon />}
            size="xs"
          >
            {previewMode ? (
              <Trans message="Close preview" />
            ) : (
              <Trans message="Logout" />
            )}
          </Button>
        </header>

        <main className="overflow-hidden rounded-panel border bg-paper shadow-xl lg:grid lg:grid-cols-[minmax(280px,0.78fr)_minmax(0,1.35fr)]">
          <aside className="relative overflow-hidden border-b bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-24 sm:p-32 lg:border-b-0 lg:border-r lg:p-40">
            <div className="pointer-events-none absolute -right-80 -top-80 size-240 rounded-full border-[36px] border-primary/5" />
            <div className="relative">
              <div className="mb-24 flex size-52 items-center justify-center rounded-full bg-primary text-on-primary shadow-md">
                <LockIcon />
              </div>
              <div className="mb-10 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                <Trans message="Before you continue" />
              </div>
              <h1 className="mb-12 text-2xl font-semibold leading-tight sm:text-3xl">
                {config.title?.trim() ? (
                  config.title
                ) : (
                  <Trans message="Terms & Conditions" />
                )}
              </h1>
              <p className="max-w-360 text-sm leading-relaxed text-muted">
                <Trans message="Please read the document below and accept it to continue." />
              </p>

              <div className="mt-32 space-y-12">
                <GuideStep
                  number="1"
                  message={<Trans message="Read the complete document" />}
                />
                <GuideStep
                  number="2"
                  message={<Trans message="Confirm your agreement" />}
                />
                <GuideStep
                  number="3"
                  message={<Trans message="Continue securely to your drive" />}
                />
              </div>
            </div>
          </aside>

          <section className="p-20 sm:p-32 lg:p-40">
            <div className="mb-16 flex flex-wrap items-center justify-between gap-10">
              <div className="flex items-center gap-10">
                <div className="flex size-38 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <DescriptionIcon size="sm" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    <Trans message="Agreement document" />
                  </div>
                  <div className="text-xs text-muted">
                    <Trans
                      message="Version :version"
                      values={{version: Number(config.version ?? 1)}}
                    />
                  </div>
                </div>
              </div>
              <div className="rounded-full border bg-alt px-10 py-5 text-xs font-semibold text-muted">
                {Math.round(scrollProgress)}%
              </div>
            </div>

            <div className="overflow-hidden rounded-panel border bg-alt/40 shadow-inner">
              <div
                ref={scrollerRef}
                onScroll={checkReachedEnd}
                className="h-[42vh] max-h-500 min-h-240 overflow-y-auto whitespace-pre-wrap bg-paper/70 p-18 text-sm leading-7 sm:p-24"
              >
                {config.body}
              </div>
              <div
                className="h-4 bg-alt"
                role="progressbar"
                aria-label="Reading progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(scrollProgress)}
              >
                <div
                  className="h-full bg-gradient-to-r from-primary to-positive transition-[width] duration-200"
                  style={{width: `${scrollProgress}%`}}
                />
              </div>
            </div>

            {!reachedEnd && (
              <div className="mt-10 flex items-center gap-6 text-xs text-muted">
                <ArrowDownwardIcon size="xs" />
                <Trans message="Scroll to the end of the document to enable the checkbox." />
              </div>
            )}

            <div className="mt-20 space-y-16 rounded-panel border bg-alt/40 p-16 sm:p-20">
              <Checkbox
                checked={agreed}
                disabled={!reachedEnd}
                onChange={e => setAgreed(e.target.checked)}
              >
                <span className="font-medium">
                  <Trans message="I have read and agree to the terms and conditions." />
                </span>
              </Checkbox>
              <Checkbox
                checked={emailCopy}
                onChange={e => setEmailCopy(e.target.checked)}
              >
                <Trans message="Email me a copy for my records." />
              </Checkbox>
            </div>

            <Button
              variant="flat"
              color="primary"
              size="md"
              className="mt-20 w-full shadow-md"
              disabled={previewMode || !agreed || accept.isPending}
              onClick={() => !previewMode && accept.mutate()}
              startIcon={<TaskAltIcon />}
            >
              {previewMode ? (
                <Trans message="Preview only" />
              ) : accept.isPending ? (
                <Trans message="Saving..." />
              ) : (
                <Trans message="Accept and continue" />
              )}
            </Button>

            <div className="mt-12 flex items-center justify-center gap-5 text-center text-xs text-muted">
              <LockIcon size="xs" />
              <Trans message="Your acceptance is securely recorded." />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

function GuideStep({number, message}: {number: string; message: ReactNode}) {
  return (
    <div className="flex items-center gap-10">
      <div className="flex size-28 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-xs font-bold text-primary">
        {number}
      </div>
      <div className="text-sm font-medium text-main">{message}</div>
    </div>
  );
}
